import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import {
  ServiceStep, ProfessionalStep, ScheduleStep, ConfirmStep,
} from "@/pages/AppointmentsPage";

/**
 * Public "Via Rápida" booking flow.
 * Route: /agendamento (no auth required).
 * Guest identifies themselves only at the Review step (name + whatsapp).
 * Backend auto-creates a lead patient tagged Lead_Via_Rapida.
 */
export default function PublicAppointmentsPage() {
  const [step, setStep] = useState("service");
  const [services, setServices] = useState([]);
  const [professional, setProfessional] = useState(null);
  const [slot, setSlot] = useState(null);
  useSeo({
    title: "Tratar Colesterol Alto em Tucuruí-PA | Agende sua Avaliação",
    description: "Agende hoje sua avaliação com farmacêutico clínico em Tucuruí-PA para tratar colesterol alto, fazer reposição de vitamina D em Tucuruí e B12 e iniciar seu acompanhamento metabólico.",
    keywords: "tratar colesterol alto, reposição vitamina D Tucuruí, colesterol alto tratamento natural, reposição vitamina D e B12, farmacêutico clínico Tucuruí-PA, acompanhamento metabólico Pará",
    path: "/agendamento",
  });

  const reset = () => { setStep("service"); setServices([]); setProfessional(null); setSlot(null); };

  const toggleService = (s) => {
    setServices((prev) => (prev.some((x) => x.id === s.id) ? prev.filter((x) => x.id !== s.id) : [...prev, s]));
  };

  const inner = () => {
    if (step === "service") return (
      <ServiceStep
        selected={services}
        onToggle={toggleService}
        onNext={() => {
          if (services.length === 0) { toast.error("Selecione ao menos um serviço."); return; }
          setStep("professional");
        }}
      />
    );
    if (step === "professional") return (
      <ProfessionalStep services={services} onBack={() => setStep("service")} onPick={(p) => { setProfessional(p); setStep("schedule"); }} />
    );
    if (step === "schedule") return (
      <ScheduleStep services={services} professional={professional} onBack={() => setStep("professional")} onPick={(s) => { setSlot(s); setStep("confirm"); }} />
    );
    if (step === "confirm") return (
      <ConfirmStep
        services={services}
        professional={professional}
        slot={slot}
        guestMode
        onBack={() => setStep("schedule")}
        onConfirmed={reset}
      />
    );
    return null;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="public-appointments-page">
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="brand-wordmark uppercase text-sm font-bold text-primary tracking-tight">
            Centro de Performance Farmacêutica
          </Link>
          <div className="flex items-center gap-2 text-xs">
            <Link to="/login" className="text-primary hover:underline" data-testid="public-appointments-login">
              Entrar
            </Link>
            <span className="text-muted-foreground">·</span>
            <Link to="/cadastro" className="text-primary hover:underline" data-testid="public-appointments-signup">
              Criar conta
            </Link>
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-6">
        {inner()}
      </main>
    </div>
  );
}
